import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, HardHat } from 'lucide-react';
import { obtenerDesempeno } from '@/api/desempeno.api';
import { ApiError } from '@/api/client';
import {
  COLUMNAS_DESEMPENO,
  CHIPS_DE_RANGO,
  alternarOrden,
  colorDelIndice,
  escribirOrden,
  garantiasPor10,
  leerOrden,
  ordenarDesempeno,
  rangoAConsulta,
  textoDelIndice,
  type ColorDelIndice,
  type ColumnaDesempeno,
} from '@/lib/desempeno';
import { rangoInicialDeDesempeno } from '@/lib/vista-por-rol';
import { ESQUEMA_DESEMPENO } from '@/lib/filtros-esquemas';
import { useFiltrosUrl } from '@/hooks/useFiltrosUrl';
import type { TokenRango } from '@/lib/rango-fechas';
import { PageHeader } from '@/components/common/PageHeader';
import { Card, CardBody } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { EmptyState } from '@/components/ui/EmptyState';
import { Cargando } from '@/components/common/Cargando';
import { cn } from '@/lib/cn';
import { useAuthStore } from '@/store/auth.store';

/** Los filtros que esta pantalla guarda en la URL. */
interface FiltrosDesempeno {
  rango?: string;
  desde?: string;
  hasta?: string;
  orden?: string;
}

/** El chip de color del índice. El gris es "no se puede saber", no "bien". */
const COLOR_A_BADGE: Record<ColorDelIndice, 'success' | 'warning' | 'danger' | 'default'> = {
  verde: 'success',
  ambar: 'warning',
  rojo: 'danger',
  gris: 'default',
};

/**
 * DESEMPEÑO DE TÉCNICOS.
 *
 * Contesta UNA pregunta: cuántas garantías está dejando cada técnico por cada
 * 10 instalaciones que hace. Hasta ahora eso se sacaba a ojo, contando
 * garantías en la lista de trabajos — y contarlas crudas premia al que instala
 * poco.
 *
 * Todo lo que se decide vive en `lib/desempeno` (el índice, el semáforo, el
 * orden y la traducción del rango). Acá solo se pinta.
 */
export default function Desempeno() {
  const miRol = useAuthStore((s) => s.user?.role);
  const { filtros, setFiltros } = useFiltrosUrl<FiltrosDesempeno>(ESQUEMA_DESEMPENO);

  // Con qué ventana abre según quién mira. Ver `lib/vista-por-rol`.
  const rangoInicial = rangoInicialDeDesempeno({ rol: miRol });

  // "Rango…" es un modo, no un token: se pide a mano y se queda hasta que se
  // toque otro chip, aunque todavía no haya ninguna fecha escrita.
  const [pidioRangoLibre, setPidioRangoLibre] = useState(false);
  const hayFechas = Boolean(filtros.desde ?? filtros.hasta);
  const modoLibre = pidioRangoLibre || (!filtros.rango && hayFechas);
  const tokenActivo: TokenRango | null = modoLibre
    ? null
    : ((filtros.rango as TokenRango | undefined) ?? rangoInicial);

  function elegirChip(token: TokenRango | null) {
    if (token === null) {
      setPidioRangoLibre(true);
      setFiltros({ rango: undefined });
      return;
    }
    setPidioRangoLibre(false);
    // Las fechas sueltas se borran: dejarlas sería tener dos rangos puestos y
    // que gane uno en silencio.
    setFiltros({ rango: token, desde: undefined, hasta: undefined });
  }

  const consulta = rangoAConsulta({
    rango: tokenActivo ?? undefined,
    desde: filtros.desde,
    hasta: filtros.hasta,
  });

  const orden = leerOrden(filtros.orden);
  function ordenarPor(columna: ColumnaDesempeno) {
    setFiltros({ orden: escribirOrden(alternarOrden(orden, columna)) });
  }

  const { data, isLoading, error } = useQuery({
    queryKey: ['tecnicos', 'desempeno', consulta],
    queryFn: () => obtenerDesempeno(consulta),
    // Sin reintentos: si el endpoint todavía no existe, reintentar tres veces
    // solo hace esperar más para leer el mismo cartel.
    retry: false,
  });

  // El endpoint lo está construyendo otra tanda. Mientras no esté, la pantalla
  // se abre y lo dice; no se muestra "error del sistema" por algo esperado.
  const todaviaNoEsta = error instanceof ApiError && error.status === 404;
  const filas = ordenarDesempeno(data ?? [], orden);

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title="Desempeño de técnicos"
        helpKey="desempeno"
        description="Cuántas garantías está dejando cada técnico por cada 10 instalaciones que hace."
      />

      <div data-guia="desempeno.rango" className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1 rounded-md border border-surface-border bg-white p-1 dark:border-slate-700 dark:bg-slate-900">
          {CHIPS_DE_RANGO.map((chip) => (
            <button
              key={chip.etiqueta}
              type="button"
              onClick={() => elegirChip(chip.token)}
              className={cn(
                'rounded px-3 py-1.5 text-xs font-medium transition-colors',
                tokenActivo === chip.token
                  ? 'bg-brand-600 text-white'
                  : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800',
              )}
            >
              {chip.etiqueta}
            </button>
          ))}
        </div>

        {modoLibre && (
          <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
            <label className="flex items-center gap-1.5">
              <span className="text-xs text-slate-400">Desde</span>
              <input
                type="date"
                value={filtros.desde ?? ''}
                onChange={(e) => setFiltros({ desde: e.target.value || undefined })}
                className="h-9 rounded-md border border-surface-border bg-white px-2 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
              />
            </label>
            <label className="flex items-center gap-1.5">
              <span className="text-xs text-slate-400">Hasta</span>
              <input
                type="date"
                value={filtros.hasta ?? ''}
                onChange={(e) => setFiltros({ hasta: e.target.value || undefined })}
                className="h-9 rounded-md border border-surface-border bg-white px-2 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
              />
            </label>
          </div>
        )}

        <span className="text-xs text-slate-400">
          Del {consulta.desde} al {consulta.hasta}
        </span>
      </div>

      <Card>
        <CardBody className="p-0">
          {isLoading ? (
            <Cargando />
          ) : todaviaNoEsta ? (
            // A7 · El vacío dice qué pasa, sin hablar como sistema.
            <EmptyState
              icon={HardHat}
              title="Esta pantalla todavía no tiene de dónde sacar los números"
              description="El cálculo del desempeño se está terminando. Cuando esté, esta misma pantalla lo muestra sola: no hay que hacer nada."
              className="m-5"
            />
          ) : error ? (
            <EmptyState
              icon={HardHat}
              title="No se pudieron traer los números"
              description="Vuelva a entrar en un momento. Si sigue igual, avísele a quien le da soporte."
              className="m-5"
            />
          ) : filas.length === 0 ? (
            <EmptyState
              icon={HardHat}
              title="Ningún técnico trabajó en estas fechas"
              description="Pruebe con «Este mes», o ponga un rango más largo."
              className="m-5"
            />
          ) : (
            <div data-guia="desempeno.tabla" className="overflow-x-auto">
              <table className="w-full min-w-[720px] text-sm">
                <thead className="bg-slate-50 text-left dark:bg-slate-900">
                  <tr>
                    {COLUMNAS_DESEMPENO.map((c) => (
                      <th
                        key={c.clave}
                        className={cn(
                          'whitespace-nowrap border-b border-surface-border px-3 py-2 font-semibold text-slate-600 dark:border-slate-800 dark:text-slate-300',
                          c.numerica && 'text-right',
                        )}
                      >
                        <button
                          type="button"
                          onClick={() => ordenarPor(c.clave)}
                          className={cn(
                            'flex items-center gap-1 hover:text-slate-900 dark:hover:text-slate-100',
                            c.numerica && 'ml-auto',
                          )}
                        >
                          {c.etiqueta}
                          {orden.columna === c.clave &&
                            (orden.direccion === 'asc' ? (
                              <ArrowUp className="h-3.5 w-3.5" />
                            ) : (
                              <ArrowDown className="h-3.5 w-3.5" />
                            ))}
                        </button>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-surface-border dark:divide-slate-800">
                  {filas.map((f) => {
                    const indice = garantiasPor10(f.instalaciones, f.garantias);
                    return (
                      <tr
                        key={f.tecnicoId}
                        className="hover:bg-slate-50 dark:hover:bg-slate-800/50"
                      >
                        <td className="px-3 py-2 font-medium text-slate-800 dark:text-slate-100">
                          {f.tecnico}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-slate-700 dark:text-slate-200">
                          {f.instalaciones}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-slate-700 dark:text-slate-200">
                          {f.garantias}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <Badge variant={COLOR_A_BADGE[colorDelIndice(indice)]}>
                            {textoDelIndice(indice)}
                          </Badge>
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-slate-700 dark:text-slate-200">
                          {f.fichasIncompletas}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      <p className="text-xs text-slate-400">
        Verde hasta 1 garantía por cada 10 instalaciones, ámbar de 2 a 3, rojo de 4 en adelante. Un
        técnico sin instalaciones en el rango no tiene número y queda al final.
      </p>
    </div>
  );
}
